import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./thankyou.css";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { getHealthSprintStageContext } from "../../functions/healthSprintStageContext";
import { updateStageFeedback } from "../../functions/updateStageFeedback";
import { useThemeIconUrls, ThemeIconImg } from "../../functions/healthSprintThemeIcons";
import { isFromMobileApp, navigateExternalUrl, isExtramileplayUrl } from "../../functions/navigationHelper";

/** Maps final meter score (0–100) to theme keys: 0–29, 30–59, 60–100. */
function scoreRangeKey(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return "range_0_30";
  if (n < 30) return "range_0_30";
  if (n < 60) return "range_30_60";
  return "range_60_plus";
}

function pickCategoryMessage(categoryVal, score) {
  if (categoryVal == null) return "";
  const key = scoreRangeKey(score);
  if (Array.isArray(categoryVal)) {
    return String(categoryVal[0]?.message ?? "");
  }
  if (typeof categoryVal === "object") {
    return String(categoryVal[key] ?? categoryVal.range_60_plus ?? "");
  }
  return String(categoryVal);
}

/** Theme fields aligned with Health Sprint (main box + meter colors from DB). */
function thankYouThemeVisuals(themeData) {
  const t = themeData || {};
  const c = t.colors && typeof t.colors === "object" && !Array.isArray(t.colors) ? t.colors : {};
  const root = (snake, camel) => {
    const v = t[snake] ?? t[camel];
    if (v != null && String(v).trim() !== "") return String(v).trim();
    return null;
  };
  const col = (snake, camel) => {
    const v = c[snake] ?? c[camel];
    if (v != null && String(v).trim() !== "") return String(v).trim();
    return null;
  };
  return {
    cardBg:
      root("main_color_box", "mainColorBox") ||
      col("main_color_box", "mainColorBox") ||
      "rgba(0, 0, 0, 0.06)",
    labelColor:
      root("heading_text_color", "headingTextColor") ||
      col("text_color", "textColor") ||
      "#000",
    energyBar: root("energy_color", "energyColor") || col("ui_color_1", "uiColor1") || "#e3e32f",
    timeBar: root("time_color", "timeColor") || col("option_color", "optionColor") || "#6fa7e3",
    heartBar: root("heart_color", "heartColor") || col("ui_color_2", "uiColor2") || "#d8381f",
    submitBtnBg:
      root("box_bg_color", "boxBgColor") || col("box_bg_color", "boxBgColor") || "#ffffff",
    submitBtnText:
      root("box_text_color", "boxTextColor") || col("box_text_color", "boxTextColor") || "#000000",
  };
}

function clampScorePct(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, n));
}

/** Avoid re-showing feedback CTA after refresh once feedback was saved for this stage. */
function thankYouFeedbackStorageKey(userId, stageId) {
  const uid = userId != null && String(userId).trim() !== "" ? String(userId).trim() : "";
  const sid = stageId != null && String(stageId).trim() !== "" ? String(stageId).trim() : "";
  if (!uid || !sid) return null;
  return `hs_thankyou_feedback_done_v1_${uid}_${sid}`;
}

function readThankYouFeedbackSubmitted(userId, stageId) {
  void thankYouFeedbackStorageKey(userId, stageId);
  return false;
}

function writeThankYouFeedbackSubmitted(userId, stageId) {
  void thankYouFeedbackStorageKey(userId, stageId);
}

function ThankYou() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const themeData = useSelector((state) => state.theme?.data);
  const state = location.state || {};
  
  // Detect if running from mobile app wrapper
  const fromMobileAppFlag = useMemo(
    () => isFromMobileApp(location.search, user),
    [location.search, user]
  );

  const [rating, setRating] = useState(3);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const stageId = useMemo(() => {
    const s = state.stageId;
    if (s != null && s !== "") {
      const n = Number(s);
      return Number.isFinite(n) ? n : null;
    }
    return getHealthSprintStageContext()?.stageId ?? null;
  }, [state.stageId]);

  useEffect(() => {
    if (readThankYouFeedbackSubmitted(user?.userId, stageId)) {
      setFeedbackSubmitted(true);
    }
  }, [user?.userId, stageId]);

  const scores = useMemo(
    () => ({
      energy: Number(state.energy),
      time: Number(state.time),
      health: Number(state.health),
    }),
    [state.energy, state.time, state.health]
  );

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  /** Exit URL: parent portal / org — never in-app routes like /health-sprint (avoids replay). */
  const safeExitUrl = useMemo(() => {
    const u = user?.backButtonRedirect;
    if (u && String(u).trim() !== "") return String(u).trim();
    const base = process.env.REACT_APP_BASE_URL;
    if (base && String(base).trim() !== "") return String(base).replace(/\/$/, "");
    return "/login";
  }, [user?.backButtonRedirect]);

  useEffect(() => {
    const prev = user?.backButtonRedirect ?? null;
    dispatch(setBackButtonUrl(safeExitUrl));
    return () => {
      if (prev) dispatch(setBackButtonUrl(prev));
    };
  }, [dispatch, user?.backButtonRedirect, safeExitUrl]);

  /** Browser “back” must not return to /health-sprint to play again — send user to safe exit. */
  useEffect(() => {
    const onPopState = () => {
      if (isExtramileplayUrl(safeExitUrl) && fromMobileAppFlag) {
        navigateExternalUrl(safeExitUrl, fromMobileAppFlag, "replace");
      } else {
        window.location.replace(safeExitUrl);
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [safeExitUrl, fromMobileAppFlag]);

  useEffect(() => {
    const e = Number(state.energy);
    const t = Number(state.time);
    const h = Number(state.health);
    if (![e, t, h].every((x) => Number.isFinite(x))) {
      navigate("/health-sprint", { replace: true });
    }
  }, [state.energy, state.time, state.health, navigate]);

  const persistFeedback = useCallback(
    async (value) => {
      if (!user?.userId || !adminToken || stageId == null) return;
      const result = await updateStageFeedback({
        userId: user.userId,
        token: adminToken,
        stageId,
        winner: value,
      });
      // eslint-disable-next-line no-console
      console.log(
        "[ThankYou feedback] Saved to database → stages.winner =",
        value,
        "| stageId =",
        stageId,
        "| response =",
        result
      );
    },
    [user?.userId, adminToken, stageId]
  );

  const canSubmitFeedback = Boolean(user?.userId && adminToken && stageId != null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const gameRoot = document.getElementById("game");
    if (gameRoot) gameRoot.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (!feedbackOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setFeedbackOpen(false);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [feedbackOpen]);

  const onRatingChange = (e) => {
    const v = Number(e.target.value);
    if (Number.isFinite(v)) setRating(v);
  };

  const handleFeedbackSubmit = useCallback(async () => {
    if (!canSubmitFeedback || feedbackSaving) return;
    setFeedbackSaving(true);
    try {
      await persistFeedback(rating);
      writeThankYouFeedbackSubmitted(user?.userId, stageId);
      setFeedbackSubmitted(true);
      setFeedbackOpen(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[ThankYou feedback] Save failed:", err?.message, err);
    } finally {
      setFeedbackSaving(false);
    }
  }, [canSubmitFeedback, feedbackSaving, persistFeedback, rating, user?.userId, stageId]);

  const toggleOn = themeData?.thankyou_toggle === "true" || themeData?.thankyou_toggle === true;
  const rawMsg = themeData?.thankyou_message;

  const energyText = pickCategoryMessage(rawMsg?.energy, scores.energy);
  const timeText = pickCategoryMessage(rawMsg?.time, scores.time);
  const heartText = pickCategoryMessage(rawMsg?.heart, scores.health);

  const visuals = useMemo(() => thankYouThemeVisuals(themeData), [themeData]);
  const iconUrls = useThemeIconUrls(themeData);

  const heading = themeData?.custom_text_thank_you_page || "Thank you";
  const textColor = visuals.labelColor;
  const accent = "#2563eb";
  const tooltipPct = ((rating - 1) / 4) * 100;
  const fillPct = ((rating - 1) / 4) * 100;
  const isMaxRating = rating >= 5;
  /** Slider thumb: happy faces only (1–5), no sad emoji. */
  const ratingHappyEmojis = ["🙂", "😊", "😄", "😁", "🤩"];
  const thumbEmoji = ratingHappyEmojis[Math.min(4, Math.max(0, rating - 1))];

  return (
    <div className={`thank-you-container${toggleOn ? " thank-you-container--toggle-on" : ""}`}>
      {!toggleOn && (
        <div className="thank-you-text thank-you-heading" style={{ color: textColor }}>
          {heading}
        </div>
      )}

      {toggleOn && (
        <div className="thank-you-meter-cards" role="list">
          {[
            {
              key: "energy",
              label: "Energy",
              score: scores.energy,
              body: energyText,
              barColor: visuals.energyBar,
              iconFallback: "⚡",
            },
            {
              key: "time",
              label: "Time",
              score: scores.time,
              body: timeText,
              barColor: visuals.timeBar,
              iconFallback: "⏱",
            },
            {
              key: "heart",
              label: "Heart",
              score: scores.health,
              body: heartText,
              barColor: visuals.heartBar,
              iconFallback: "❤️",
            },
          ].map(({ key, label, score, body, barColor, iconFallback }) => {
            const pct = clampScorePct(score);
            const bodyTrim = String(body || "").trim();
            return (
              <section
                key={key}
                className="thank-you-meter-card"
                role="listitem"
                style={{
                  background: visuals.cardBg,
                  color: textColor,
                  borderColor: barColor,
                }}
              >
                <div className="thank-you-meter-card-icon" aria-hidden="true">
                  <ThemeIconImg src={iconUrls[key]} fallback={iconFallback} alt="" />
                </div>
                <h3 className="thank-you-meter-card-heading" style={{ color: textColor }}>
                  {label}{" "}
                  <span className="thank-you-meter-card-points">({Math.round(pct)}%)</span>
                </h3>
                <div
                  className="thank-you-score-bar"
                  role="meter"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(pct)}
                  aria-label={`${label} score ${Math.round(pct)} out of 100`}
                >
                  <div
                    className="thank-you-score-bar-fill"
                    style={{ width: `${pct}%`, background: barColor }}
                  />
                </div>
                {bodyTrim ? (
                  <p className="thank-you-meter-card-body" style={{ color: textColor }}>
                    {bodyTrim}
                  </p>
                ) : null}
              </section>
            );
          })}
        </div>
      )}

      <div className="thank-you-feedback-trigger-wrap">
        {feedbackSubmitted ? (
          <p className="thank-you-feedback-thanks" style={{ color: textColor }}>
            Thank you for your feedback.
          </p>
        ) : (
          <button
            type="button"
            className="thank-you-rate-btn"
            style={{
              background: visuals.cardBg,
              color: textColor,
              borderColor: textColor,
            }}
            onClick={() => setFeedbackOpen(true)}
          >
            How was your sprint
          </button>
        )}
      </div>

      {feedbackOpen && (
        <div
          className="thank-you-modal-backdrop"
          role="presentation"
          onClick={() => !feedbackSaving && setFeedbackOpen(false)}
        >
          <div
            className="thank-you-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="thank-you-feedback-dialog-title"
            style={{
              background: visuals.cardBg,
              color: textColor,
              borderColor: textColor,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="thank-you-feedback-dialog-title" className="thank-you-modal-title" style={{ color: textColor }}>
              Feedback on your experience
            </h2>
            <div className="thank-you-feedback thank-you-feedback--in-modal" style={{ "--thank-you-rating-accent": accent }}>
              <div className="thank-you-rating-row" dir="ltr">
                <span
                  className="thank-you-rating-end thank-you-rating-end--start"
                  style={{ color: textColor }}
                >
                  <span className="thank-you-rating-start-num">1</span>
                  <span className="thank-you-rating-start-emoji" aria-hidden="true">
                    🙂
                  </span>
                </span>
                <div
                  className="thank-you-rating-slider-col"
                  style={{ "--thank-you-thumb-pct": `${tooltipPct}%` }}
                >
                  <div
                    className={`thank-you-rating-tooltip${isMaxRating ? " thank-you-rating-tooltip--finish" : ""}`}
                    style={{
                      left: `${tooltipPct}%`,
                      background: textColor,
                      color: "black",
                    }}
                    aria-hidden="true"
                  >
                    {rating}
                  </div>
                  <div className="thank-you-rating-track-wrap">
                    <span
                      className="thank-you-rating-thumb-emoji"
                      style={{ color: textColor }}
                      aria-hidden="true"
                    >
                      {thumbEmoji}
                    </span>
                    <input
                      type="range"
                      className="thank-you-rating-input"
                      min={1}
                      max={5}
                      step={1}
                      value={rating}
                      onChange={onRatingChange}
                      disabled={!canSubmitFeedback}
                      aria-label="How was your sprint: rate from 1 to 5"
                      style={{
                        "--thank-you-fill-pct": `${fillPct}%`,
                        "--thank-you-rating-accent": textColor,
                      }}
                    />
                  </div>
                </div>
                <span
                  className="thank-you-rating-end thank-you-rating-end--finish"
                  style={{ color: textColor }}
                >
                  <span className="thank-you-rating-finish-num">5</span>
                  <span className="thank-you-rating-finish-emoji" aria-hidden="true">
                    🤩
                  </span>
                </span>
              </div>
            </div>
            {!canSubmitFeedback ? (
              <p className="thank-you-modal-hint" style={{ color: textColor }}>
                Feedback cannot be saved right now (missing sign-in or stage).
              </p>
            ) : null}
            <div className="thank-you-modal-actions">
              <button
                type="button"
                className="thank-you-modal-btn thank-you-modal-btn--secondary"
                style={{ color: textColor, borderColor: textColor }}
                disabled={feedbackSaving}
                onClick={() => setFeedbackOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="thank-you-modal-btn thank-you-modal-btn--primary"
                style={{
                  background: visuals.submitBtnBg,
                  color: visuals.submitBtnText,
                  borderColor: visuals.submitBtnText,
                }}
                disabled={!canSubmitFeedback || feedbackSaving}
                onClick={() => void handleFeedbackSubmit()}
              >
                {feedbackSaving ? "Submitting…" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ThankYou;
